import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";

export interface ModernPaginationProps {
  count: number;
  page: number;
  limit?: number;
  data?: any[];
  loading?: boolean;
  onPageChange: (page: number) => void;
  className?: string;
}

export const ModernPagination: React.FC<ModernPaginationProps> = ({
  count = 0,
  page = 1,
  limit = 10,
  data = [],
  loading = false,
  onPageChange,
  className,
}) => {
  const currentPage = Math.max(1, Number(page) || 1);
  const totalPages = Math.max(1, Math.ceil(count / limit));

  const fromItem = count === 0 ? 0 : (currentPage - 1) * limit + 1;
  const toItem = Math.min(
    count,
    (currentPage - 1) * limit + (Array.isArray(data) ? data.length : limit)
  );

  // Generate pagination items with ellipses
  const paginationRange = useMemo(() => {
    const totalNumbers = 7;
    if (totalPages <= totalNumbers) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - 1, 1);
    const rightSiblingIndex = Math.min(currentPage + 1, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 4;
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, "DOTS_RIGHT", totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 4;
      const rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      );
      return [1, "DOTS_LEFT", ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = [currentPage - 1, currentPage, currentPage + 1];
      return [1, "DOTS_LEFT", ...middleRange, "DOTS_RIGHT", totalPages];
    }

    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages, currentPage]);

  if (count <= 0) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row items-center justify-between gap-4 py-3 px-2 border-t border-slate-100 text-sm",
        className
      )}
    >
      {/* Results Count Text */}
      <div className="text-sm text-slate-600 font-medium">
        Showing <span className="font-semibold text-slate-800">{fromItem}</span>{" "}
        to <span className="font-semibold text-slate-800">{toItem}</span> of{" "}
        <span className="font-semibold text-slate-800">{count}</span> results
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center gap-1.5 select-none">
        {/* Previous Button */}
        <Button
          type="button"
          onClick={() => {
            if (currentPage > 1 && !loading) {
              onPageChange(currentPage - 1);
            }
          }}
          disabled={currentPage <= 1 || loading}
          className={cn(
            "flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold border transition-all duration-150",
            currentPage <= 1 || loading
              ? "opacity-40 cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-300 shadow-xs cursor-pointer active:scale-95"
          )}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {paginationRange.map((item, idx) => {
            if (item === "DOTS_LEFT" || item === "DOTS_RIGHT") {
              return (
                <span
                  key={`dots-${idx}`}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 font-bold text-xs"
                >
                  •••
                </span>
              );
            }

            const pageNum = Number(item);
            const isActive = pageNum === currentPage;

            return (
              <Button
                key={`page-${pageNum}`}
                type="button"
                onClick={() => {
                  if (!isActive && !loading) {
                    onPageChange(pageNum);
                  }
                }}
                disabled={loading}
                className={cn(
                  "w-8 h-8 sm:w-9 sm:h-9 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 flex items-center justify-center",
                  isActive
                    ? "bg-[#006747] text-white shadow-xs font-bold ring-2 ring-[#006747]/20"
                    : "border border-slate-200/90 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-300 shadow-2xs active:scale-95 cursor-pointer"
                )}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>

        {/* Next Button */}
        <Button
          type="button"
          onClick={() => {
            if (currentPage < totalPages && !loading) {
              onPageChange(currentPage + 1);
            }
          }}
          disabled={currentPage >= totalPages || loading}
          className={cn(
            "flex items-center gap-1 h-9 px-3 rounded-lg text-xs font-semibold border transition-all duration-150",
            currentPage >= totalPages || loading
              ? "opacity-40 cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:border-slate-300 shadow-xs cursor-pointer active:scale-95"
          )}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default ModernPagination;
